import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Beacon} from "types/Beacon";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import {formattedDate} from "services/date";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteBeacon from "pages/Beacon/Delete";
import "services/mabox-gl";
import BatteryLevel from "components/BatteryLevel";
import BooleanIndicator from "components/BooleanIndicator";
import {feature, metersToPixelsAtMaxZoom} from "services/geometry";
import {useOperator} from "../../../context/OperatorContext";
import Map from "../../../components/Map";
import {Layer, Source} from 'react-map-gl/mapbox';

// Component ----

function ViewBeacon() {
  const {settings} = useOperator();
  const navigate = useNavigate();
  const {id} = useParams();

  const [deletingBeacon, setDeletingBeacon] = useState<Beacon | null>(null);

  const {data: beacon, isLoading: beaconIsLoading} = useQuery(
    ['beacon', {id}],
    () => ngage.beacons.get(Number(id))
  )

  const beaconFeature = beacon ? feature(beacon.id, {}, beacon.position) : undefined

  // Prepare breadcrumbs
  const breadcrumbs = {
    "Hardware": "/",
    "Beacons": "/beacons",
    [beacon?.name || 'View Beacon']: `/beacons/${id}`
  }

  // Prepare action handlers
  function handleDeleteBeacon() {
    if (beacon) setDeletingBeacon(beacon)
  }

  function handleEditBeacon() {
    if (id) navigate(`/beacons/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteBeacon}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditBeacon}>Edit</Button>
  </>

  return (
    <Page title={beacon?.name || 'View Beacon'} breadcrumbs={breadcrumbs} actions={actions} isLoading={beaconIsLoading}>
      {!!beacon &&
        <>
          {/* Beacon Information */}
          <section>
            <Card
              heading="Beacon Information"
              subheading={`Added on ${formattedDate(beacon.created_at)}`}
            >
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Enabled</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={beacon.is_enabled}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Battery</DescriptionTerm>
                  <DescriptionDetails>
                    <BatteryLevel percent={beacon.battery_level}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>MAC Address</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.mac_address.toUpperCase()}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Make & Model</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.make} {beacon.model}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Version</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.version}
                  </DescriptionDetails>
                </DescriptionListItem>

              </DescriptionList>
            </Card>
          </section>

          {/* Configuration */}
          <section className="mt-4">
            <Card heading="Configuration">
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Type</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.frame_type}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Namespace</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.namespace}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Instance</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.instance}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>RSSI @ 0m</DescriptionTerm>
                  <DescriptionDetails>
                    -24 dBm
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Transmission Power</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.tx_power} dBm
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Advert Interval</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.interval} ms
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Location */}
          <section className="mt-4">
            <Card heading="Location">
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Site</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.site.name}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Topic</DescriptionTerm>
                  <DescriptionDetails>
                    {beacon.topic?.name ?? 'N/A'}
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Map */}
          {beacon.position &&
            <section className="mt-4 rounded-md shadow-sm overflow-hidden">
              <Map
                height="32rem"
                defaultMapStyle={settings?.audience_app.map_style ?? null}
                initialViewState={{
                  longitude: beacon.position.coordinates[0],
                  latitude: beacon.position.coordinates[1],
                  zoom: 14
                }}
                hidePointsOfInterest
                childLayersFirst
              >
                <Source id="beacon" type="geojson" data={beaconFeature}>
                  <Layer
                    id="beacon-region"
                    type="circle"
                    paint={{
                      "circle-color": "#3730a3",
                      "circle-radius": 3
                    }}
                  />
                  <Layer
                    id="beacon-radius"
                    type="circle"
                    paint={{
                      "circle-color": "#3730a3",
                      "circle-opacity": 0.3,
                      "circle-radius": ["interpolate", ["linear"], ["zoom"],
                        0, 0,
                        20, metersToPixelsAtMaxZoom(10, beacon.position.coordinates[1])
                      ],
                    }}
                  />
                </Source>
              </Map>
            </section>
          }
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteBeacon
        show={beacon === deletingBeacon}
        beacon={deletingBeacon}
        onCancel={() => setDeletingBeacon(null)}
        onDelete={() => navigate('/beacons')}
        onDeleteFail={() => setDeletingBeacon(null)}
      />
    </Page>
  );
}

export default ViewBeacon;